import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MapPin, Phone, Clock, Mail, Loader2, ArrowUpRight } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in name, email, and message");
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent", { description: "We'll get back to you shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Try calling us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="eyebrow">Contact</div>
      <h1 className="mt-3 font-heading text-5xl sm:text-6xl font-light text-stone-900 tracking-tight">Say hello.</h1>
      <p className="mt-4 text-stone-600 max-w-xl">Have a question? Want to check availability before booking? We usually reply within a few hours.</p>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-6">
          <Info icon={MapPin} label="Studio" value="New No. 128, N Usman Road, T. Nagar, Chennai - 600017" />
          <Info icon={Phone} label="Call us" value="+91 7200831203" href="tel:7200831203" testid="contact-phone" />
          <Info icon={Mail} label="Email" value="afrozmeeran13@gmail.com" href="mailto:afrozmeeran13@gmail.com" testid="contact-email" />
          <Info icon={Clock} label="Hours" value={<>Open 7 days a week<br /><span className="text-stone-500 text-sm">10:00 AM – 7:00 PM</span></>} />
          <div className="aspect-[16/12] w-full border border-stone-200 overflow-hidden bg-stone-100 mt-6">
            <iframe title="Map" className="w-full h-full" loading="lazy"
              src="https://www.google.com/maps?q=New%20No.%20128%20N%20Usman%20Road%20T%20Nagar%20Chennai&output=embed"/>
          </div>
        </div>
        <div className="lg:col-span-7">
          <form onSubmit={submit} className="border border-stone-200 bg-white p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <F label="Name">
              <input data-testid="contact-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 bg-white" />
            </F>
            <F label="Email">
              <input type="email" data-testid="contact-email-input" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 bg-white" />
            </F>
            <F label="Phone (optional)">
              <input data-testid="contact-phone-input" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 bg-white" />
            </F>
            <F label="Message">
              <textarea data-testid="contact-message" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} className="w-full border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2.5 bg-white min-h-[140px]" />
            </F>
            <button disabled={sending} data-testid="contact-submit" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-900 text-stone-50 hover:bg-emerald-950 disabled:opacity-60 transition-colors duration-200">
              {sending ? <><Loader2 className="animate-spin" size={16} /> Sending...</> : <>Send message <ArrowUpRight size={16} /></>}
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function Info({ icon: Icon, label, value, href, testid }) {
  const body = (
    <>
      <span className="p-2 border border-stone-200 text-emerald-900 shrink-0"><Icon size={16} /></span>
      <div>
        <div className="eyebrow">{label}</div>
        <div className="mt-1 text-stone-800">{value}</div>
      </div>
    </>
  );
  return href
    ? <a href={href} data-testid={testid} className="flex items-start gap-4 hover:text-emerald-900">{body}</a>
    : <div className="flex items-start gap-4">{body}</div>;
}
